<template>
  <interview-layout :touch-and-validate="touchAndValidate">
    <div style="max-width: 640px;">
      <h1 class="px-4 text-primary fade-in-up-enter-active animation-slowest">Uitgevoerde maatregelen</h1>
      <h5 class="px-4 pb-3 fade-in-up-enter-active animation-slowest delay-200">Welke maatregelen heeft u al uitgevoerd?</h5>

      <base-card class="w-100" inner-class="vw-100" color="alternate" :outer-padding="true">
        <base-form @submit="onSubmit">
          <button-grid-input
            name="ExecutedMeasures"
            btn-direction="row"
            type="checkbox"
            btn-class="min-40"
            :pairs-mode="true"
            :distribute-equally="true"
            :options="executedMeasuresOptions"
            variant="minimal-filled-info"
            inactiveVariant="minimal-dark"
            v-model:value="lead.executedMeasures.ExecutedMeasures"
            @update:value="updateExecutedMeasures"
          >
            Selecteer de maatregelen die u al heeft uitgevoerd:
          </button-grid-input>

          <!-- Add description for executed measures -->
          <base-card
            color="light"
            class="mt-4 executed-measures-notes-card w-100"
            inner-class="p-3 w-100"
          >
            
            <h6 class="mb-3 text-primary">Algemene toelichting</h6>
            <base-input
              type="textarea"
              name="algemene-toelichting"
              id="algemene-toelichting"
              v-model:value="lead.executedMeasures.ExecutedMeasuresNotes['Algemene_toelichting']"
              :rows="3"
              :min-rows="3"
              :autosize="true"
              :input-class="'form-control w-100 border-color-secondary'"
              :placeholder="'Geef hier een korte toelichting'"
            />
            <div v-if="lead.executedMeasures.ExecutedMeasures && lead.executedMeasures.ExecutedMeasures.length > 0">
              <h6 class="mb-3 text-primary">Toelichting per maatregel</h6>
              <div
                v-for="measure in lead.executedMeasures.ExecutedMeasures"
                :key="measure"
                class="mb-2"
              >
                <label :for="'note-' + normalizeKey(measure)" class="fw-500 mb-2 text-secondary d-block">
                  <span v-html="getMeasureLabel(measure)"></span>
                </label>
                <base-input
                  type="textarea"
                  :name="'note-' + normalizeKey(measure)"
                  :id="'note-' + normalizeKey(measure)"
                  v-model:value="lead.executedMeasures.ExecutedMeasuresNotes[normalizeKey(measure)]"
                  :rows="3"
                  :min-rows="3"
                  :autosize="true"
                  :input-class="'form-control w-100 border-color-secondary'"
                  :placeholder="'Geef hier een korte toelichting'"
                />
              </div>
            </div>
          </base-card>

          <button-row class="mt-5">
            <base-button class="ms-auto" icon="bi-arrow-right" type="submit" :disabled="v$.executedMeasures.$invalid">Volgende</base-button>
          </button-row>
        </base-form>
      </base-card>

      <button-row class="mt-2">
        <base-button variant="minimal-secondary" icon="bi-arrow-left" icon-position="start" @click="onPrev">Terug</base-button>
      </button-row>
    </div>
  </interview-layout>
</template>

<script>
  import InterviewLayout from "@/views/layouts/InterviewLayout.vue";
  import BaseCard from "@/components/BaseCard.vue";
  import BaseForm from "@/components/BaseForm.vue";
  import BaseButton from "@/components/BaseButton.vue";
  import ButtonRow from "@/components/ButtonRow.vue";
  import ButtonGridInput from "@/components/ButtonGridInput.vue";
  import BaseInput from "@/components/BaseInput.vue";

  import { useAllStores } from "@/helpers/UseAllStores";
  import { CommonComposable } from "@/helpers/CommonComposable";
  import { FlowService } from "@/services/FlowService";
  import { getInterestDefinitions } from "@/rules/InterestDefinitions";

  export default {
    components: {
      InterviewLayout,
      BaseCard,
      BaseForm,
      BaseButton,
      ButtonRow,
      ButtonGridInput,
      BaseInput
    },
    setup(props, context) {
      return { ...CommonComposable(props, context), ...useAllStores() };
    },
    data() {
      return {
        maxCols: 1,
        btnDirection: 'column'
      };
    },
    computed: {
      executedMeasuresOptions() {
        return getInterestDefinitions(
          this.lead.meta.AllInterestsEnabled,
          this.lead.meta.InterestWhiteList
        );
      },
    },
  methods: {
      // Method used to replace spaces with underscores for keys
      normalizeKey(key) {
        const option = this.executedMeasuresOptions.find(opt => opt.value === key);
        let text = option ? option.label : key;
        text = String(text).replace(/<[^>]*>/g, '').trim();
        return text.replace(/\s+/g, '_').replace(/[^\w-]/g, '');
      },

      // Update executed measures and structure notes
      updateExecutedMeasures(selectedValues) {
        const sortedValues = this.executedMeasuresOptions
          .map((option) => option.value)
          .filter((value) => selectedValues.includes(value));

        this.lead.executedMeasures.ExecutedMeasures = sortedValues;

        const updatedNotes = { ...this.lead.executedMeasures.ExecutedMeasuresNotes };
        sortedValues.forEach((value) => {
          const normalizedKey = this.normalizeKey(value);
          if (!(normalizedKey in updatedNotes)) {
            updatedNotes[normalizedKey] = "";
          }
        });
        this.lead.executedMeasures.ExecutedMeasuresNotes = updatedNotes;
      },

      // Clean up notes for unselected measures and empty notes
      cleanMeasureNotes() {
        const cleanedNotes = {};
        const existingNotes = this.lead.executedMeasures.ExecutedMeasuresNotes || {};

        const generalKey = this.normalizeKey('Algemene toelichting');

        if (existingNotes[generalKey]?.trim() !== "") {
          cleanedNotes[generalKey] = existingNotes[generalKey];
        }

        const measures = Array.isArray(this.lead.executedMeasures.ExecutedMeasures)
          ? this.lead.executedMeasures.ExecutedMeasures
          : [];
        measures.forEach((measure) => {
          const normalizedKey = this.normalizeKey(measure);
          if (existingNotes[normalizedKey]?.trim() !== "") {
            cleanedNotes[normalizedKey] = existingNotes[normalizedKey];
          }
        });
        this.lead.executedMeasures.ExecutedMeasuresNotes = cleanedNotes;
      },

      // Get label for a given measure value
      getMeasureLabel(value) {
        const option = this.executedMeasuresOptions.find((opt) => opt.value === value);
        return option ? option.label : value;
      },
      touchAndValidate() {
        this.v$.executedMeasures.$touch();
        return !this.v$.executedMeasures.$invalid;
      },
      onSubmit() {
        if (this.touchAndValidate()) {
          this.cleanMeasureNotes();
          FlowService.pushNext(this);
        }
      },
      onPrev() {
        FlowService.pushPrev(this);
      },
      breakPoints() {
        if (window.innerWidth > 550) {
          this.maxCols = null;
          this.btnDirection = 'column';
        } else {
          this.maxCols = 1;
          this.btnDirection = 'row';
        }
      },
    },
    beforeRouteUpdate() {
      this.breakPoints();
    },
    mounted() {
      this.breakPoints();
      
      window.addEventListener("resize", this.breakPoints);
    },
    unmounted() {
      window.removeEventListener("resize", this.breakPoints);
  },

  watch: {
    'lead.executedMeasures.ExecutedMeasures': {
        handler(selected = []) {
          selected = Array.isArray(selected) ? selected : [];
          const existingNotes = this.lead.executedMeasures.ExecutedMeasuresNotes || {};

          const generalKey = this.normalizeKey('Algemene toelichting');

          // If no measures are selected, reset related fields for isolatieplan view
          if (selected.length === 0) {
            this.lead.isolatieplan.HasMeasuresForRetroactive = null;
            // this.lead.isolatieplan.DeclaredAtOtherSubsidy = null;
            // this.lead.isolatieplan.RetroactiveMeasuresTotalAmount = null;
            this.lead.isolatieplan.RetroactiveMeasures = [];

            this.lead.executedMeasures.ExecutedMeasuresNotes =
            existingNotes[generalKey] && String(existingNotes[generalKey]).trim() !== ""
              ? { [generalKey]: existingNotes[generalKey] }
              : {};
          } else {
            const notes = this.lead.executedMeasures.ExecutedMeasuresNotes;
            const filtered = {};

            if (notes[generalKey] !== undefined && String(notes[generalKey]).trim() !== "") {
              filtered[generalKey] = notes[generalKey];
            }

            selected.forEach(m => {
              const k = this.normalizeKey(m);
              if (notes[k] !== undefined) filtered[k] = notes[k];
            });
            this.lead.executedMeasures.ExecutedMeasuresNotes = filtered;
          }
        },
        deep: true
      }
  }
  };
</script>